import React, { useState, useEffect } from 'react';
import { orderService } from '../../services/orderService';
import { Order } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import { FiShoppingCart, FiCheck, FiXCircle } from 'react-icons/fi';
import Icon from '../common/Icon';
import { useOrderFormModal } from '../../hooks/useOrderFormModal';
import OrderFormModal from '../orders/OrderFormModal';
import { useAppData } from '../../context/AppDataContext';

interface CustomerOrderHistoryProps {
  customerId: number;
  onOrderSaved?: () => void;
}

const CustomerOrderHistory: React.FC<CustomerOrderHistoryProps> = ({ customerId, onOrderSaved }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { refreshCustomers } = useAppData();

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await orderService.getAll({ customer_id: customerId, limit: 500 });
      setOrders(response.data);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const orderModal = useOrderFormModal({
    onSaved: async () => {
      await loadOrders();
      onOrderSaved?.();
    },
  });

  useEffect(() => {
    loadOrders();
    refreshCustomers();
  }, [customerId]);

  const formatCurrency = (value?: number) => {
    if (value === undefined || value === null) return '—';
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const locationLabel = (order: Order) => {
    const parts = [order.district, order.destination].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : '—';
  };

  const statusIcon = (status: string | undefined, label: string) => {
    const normalized = (status || 'not_set').toLowerCase();
    const isCompleted = normalized === 'completed';
    return (
      <span
        title={`${label}: ${status || 'not set'}`}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${isCompleted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
      >
        <Icon component={isCompleted ? FiCheck : FiXCircle} className="w-3 h-3" />
      </span>
    );
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <Icon component={FiShoppingCart} className="w-5 h-5 text-green-600" />
          Order History
          {!loading && (
            <span className="text-sm font-normal text-gray-500">({orders.length})</span>
          )}
        </h3>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-8 text-gray-500">Loading orders...</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No orders for this customer yet</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order #</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Price/bag</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase" title="Payment / PO / Dispatch / Invoice">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">
                      {formatDate(order.order_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => orderModal.openForEdit(order)}
                        className="text-indigo-600 hover:text-indigo-800 font-medium"
                        title="View order details"
                      >
                        {order.order_number || `#${order.id}`}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{order.brand_name || '—'}</td>
                    <td className="px-3 py-2 text-gray-700 max-w-[10rem] truncate" title={locationLabel(order)}>
                      {locationLabel(order)}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{order.product_type || '—'}</td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {order.quantity != null ? order.quantity : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {formatCurrency(order.price_per_bag)}
                    </td>
                    <td className="px-3 py-2 text-gray-700">{order.order_type || '—'}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-center gap-1">
                        {statusIcon(order.payment_status, 'Payment')}
                        {statusIcon(order.po_status, 'PO')}
                        {statusIcon(order.dispatch_status, 'Dispatch')}
                        {statusIcon(order.invoice_status, 'Invoice')}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OrderFormModal {...orderModal} />
    </>
  );
};

export default CustomerOrderHistory;
